import { DiagnosticReporterService } from './diagnostic-reporter.service';
import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject, interval, Subscription, firstValueFrom, timeout } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { describeFailure, FailureDiagnostic, FailureStep } from './failure-diagnostics';

export interface MaintenanceStatus {
  enabled: boolean;
  upcomingBanner?: boolean;
  hardMode?: boolean;
  message: string;
  updated_at: string;
  updated_by: string;
  type?: 'planned' | 'emergency' | 'update';
  severity?: 'info' | 'warning' | 'critical';
  estimatedDuration?: number;
  scheduledStart?: string;
  scheduledEnd?: string;
  allowAdminBypass?: boolean;
  showCountdown?: boolean;
  affectedServices?: string[];
  diagnostic?: FailureDiagnostic;
}

export type MaintenanceMode = 'none' | 'planned' | 'server_error';
export type MaintenanceScope = 'full' | 'auth_only';

@Injectable({ providedIn: 'root' })
export class MaintenanceService {
  private maintenanceModeSubject = new BehaviorSubject<MaintenanceMode>('none');
  private maintenanceStatusSubject =
    new BehaviorSubject<MaintenanceStatus | null>(null);
  private maintenanceScopeSubject = new BehaviorSubject<MaintenanceScope>(
    'full'
  );
  private showAdminBannerSubject = new BehaviorSubject<boolean>(false);
  private isAdminBypassSubject = new BehaviorSubject<boolean>(false);
  private initialCheckCompleted = new BehaviorSubject<boolean>(false);
  private periodicCheckSubscription?: Subscription;
  private activeStartups = 0;
  private activeRecoveries = 0;

  get gameRecoveryActive(): boolean { return this.activeRecoveries > 0; }

  beginGameRecovery(): () => void {
    this.activeRecoveries++;
    let finished = false;
    return () => {
      if (!finished) this.activeRecoveries--;
      finished = true;
    };
  }


  get gameStartupActive(): boolean { return this.activeStartups > 0; }

  beginGameStartup(): () => void {
    this.activeStartups++;
    let finished = false;
    return () => {
      if (!finished) this.activeStartups--;
      finished = true;
    };
  }

  maintenanceMode$ = this.maintenanceModeSubject.asObservable();
  maintenanceStatus$ = this.maintenanceStatusSubject.asObservable();
  maintenanceScope$ = this.maintenanceScopeSubject.asObservable();
  showAdminBanner$ = this.showAdminBannerSubject.asObservable();
  isAdminBypass$ = this.isAdminBypassSubject.asObservable();

  constructor(private http: HttpClient, private injector: Injector, private reporter: DiagnosticReporterService) {
    // Start proactive maintenance monitoring on service initialization
    this.startMaintenanceMonitoring();
  }

  setMaintenanceMode(
    mode: MaintenanceMode,
    status?: MaintenanceStatus,
    isAdmin: boolean = false
  ) {
    // Default scope: full for server errors, auth_only for planned (non-logged users)
    const defaultScope: MaintenanceScope =
      mode === 'server_error' ? 'full' : 'full';
    this.setMaintenanceModeWithScope(mode, status, defaultScope, isAdmin);
  }

  setMaintenanceModeWithScope(
    mode: MaintenanceMode,
    status?: MaintenanceStatus,
    scope: MaintenanceScope = 'full',
    isAdmin: boolean = false
  ) {
    this.maintenanceModeSubject.next(mode);
    this.maintenanceScopeSubject.next(scope);

    if (status) {
      this.maintenanceStatusSubject.next(status);
    }

    // Handle admin bypass for planned maintenance
    if (mode === 'planned' && isAdmin) {
      this.showAdminBannerSubject.next(true);
      this.isAdminBypassSubject.next(true);
    } else {
      this.showAdminBannerSubject.next(false);
      this.isAdminBypassSubject.next(false);
    }
  }

  // Convenience method for backwards compatibility
  setMaintenanceModeBoolean(state: boolean, status?: MaintenanceStatus) {
    this.setMaintenanceMode(state ? 'planned' : 'none', status);
  }

  setServerError(error?: unknown, step: FailureStep = 'HTTP') {
    // Keep the first failure visible even if other background requests fail later.
    if (this.getCurrentMode() === 'server_error') return;
    const diagnostic = describeFailure(error, step);
    console.error('[Spiel-Fehler]', diagnostic);
    this.reporter.report(diagnostic);
    this.setMaintenanceMode('server_error', {
      enabled: true,
      message: diagnostic.message,
      updated_at: diagnostic.time,
      updated_by: 'system',
      diagnostic,
    });
  }

  setPlannedMaintenance(status: MaintenanceStatus, isAdmin: boolean = false) {
    this.setMaintenanceMode('planned', status, isAdmin);
  }

  setPlannedMaintenanceWithScope(
    status: MaintenanceStatus,
    scope: MaintenanceScope,
    isAdmin: boolean = false
  ) {
    this.setMaintenanceModeWithScope('planned', status, scope, isAdmin);
  }

  clearMaintenance() {
    this.maintenanceModeSubject.next('none');
    this.maintenanceStatusSubject.next(null);
    this.maintenanceScopeSubject.next('full');
    this.showAdminBannerSubject.next(false);
    this.isAdminBypassSubject.next(false);
  }

  private startMaintenanceMonitoring() {
    // Initial check
    this.checkAndUpdateMaintenanceStatus();

    // Periodic check every 90 seconds
    this.periodicCheckSubscription = interval(90000).subscribe(() => {
      this.checkAndUpdateMaintenanceStatus();
    });
  }

  private async checkAndUpdateMaintenanceStatus() {
    try {
      const maintenanceStatus = await this.checkMaintenanceStatus();
      // A healthy maintenance endpoint does not prove that the game can initialize.
      // Keep the diagnostic until the user explicitly retries or navigates away.
      if (!maintenanceStatus || this.getCurrentMode() === 'server_error') return;

      if (maintenanceStatus?.enabled) {
        // Check login and admin status
        const { isLoggedIn, isAdmin } = await this.getUserStatus();
        if (this.getCurrentMode() === 'server_error') return;

        if (isLoggedIn && isAdmin) {
          // Admin: show banner only
          this.setPlannedMaintenanceWithScope(maintenanceStatus, 'full', true);
        } else if (isLoggedIn) {
          // Logged in user: full maintenance
          this.setPlannedMaintenanceWithScope(maintenanceStatus, 'full', false);
        } else {
          // Not logged in: auth-only maintenance (allow browsing)
          this.setPlannedMaintenanceWithScope(
            maintenanceStatus,
            'auth_only',
            false
          );
        }
      } else if (maintenanceStatus?.upcomingBanner) {
        // Upcoming maintenance banner - just update the status without maintenance mode
        this.maintenanceStatusSubject.next(maintenanceStatus);
        this.maintenanceModeSubject.next('none');
      } else {
        // No maintenance - clear any existing maintenance mode
        const currentMode = this.maintenanceModeSubject.value;
        if (currentMode !== 'none') {
          this.clearMaintenance();
        }
        // Also clear status if no upcomingBanner
        this.maintenanceStatusSubject.next(null);
      }
    } catch (error) {
      console.warn('Maintenance status check failed:', error);
      // Don't change maintenance mode on check failure - server might be down temporarily
    } finally {
      // Mark initial check as completed
      this.initialCheckCompleted.next(true);
    }
  }

  private async getUserStatus(): Promise<{
    isLoggedIn: boolean;
    isAdmin: boolean;
  }> {
    try {
      const timeoutPromise = new Promise<{
        isLoggedIn: boolean;
        isAdmin: boolean;
      }>((resolve) => {
        setTimeout(() => resolve({ isLoggedIn: false, isAdmin: false }), 2000);
      });

      const userCheckPromise = (async () => {
        const AuthService = (await import('../auth/auth.service')).AuthService;
        const authService = this.injector.get(AuthService);
        const isLoggedIn = await authService.isAuthenticated();

        if (!isLoggedIn) {
          return { isLoggedIn: false, isAdmin: false };
        }

        const MyUserService = (await import('../my-user/my-user.service'))
          .MyUserService;
        const myUserService = this.injector.get(MyUserService);
        const user = await myUserService.getUser();
        return { isLoggedIn: true, isAdmin: user?.isAdmin || false };
      })();

      return await Promise.race([userCheckPromise, timeoutPromise]);
    } catch (error) {
      return { isLoggedIn: false, isAdmin: false };
    }
  }

  private async getAdminStatus(): Promise<boolean> {
    const { isAdmin } = await this.getUserStatus();
    return isAdmin;
  }

  setPlannedMaintenanceWithAdminCheck(status: MaintenanceStatus) {
    // This method is now mainly for external calls (like from interceptor)
    // The periodic check handles most cases
    this.getAdminStatus()
      .then((isAdmin) => {
        this.setPlannedMaintenance(status, isAdmin);
      })
      .catch(() => {
        this.setPlannedMaintenance(status, false);
      });
  }

  async checkMaintenanceStatus(): Promise<MaintenanceStatus | null> {
    try {
      // Use '/maintenance.json' which will be proxied to the testing server
      const maintenanceUrl = '/maintenance.json';
      const response = await firstValueFrom(
        this.http.get<MaintenanceStatus>(maintenanceUrl).pipe(timeout(10000))
      );
      return response || null;
    } catch (error) {
      console.warn('Failed to fetch maintenance status:', error);
      return null;
    }
  }

  private stopPeriodicCheck() {
    if (this.periodicCheckSubscription) {
      this.periodicCheckSubscription.unsubscribe();
      this.periodicCheckSubscription = undefined;
    }
  }

  getCurrentMode(): MaintenanceMode {
    return this.maintenanceModeSubject.value;
  }

  shouldShowMaintenancePage(): boolean {
    const mode = this.maintenanceModeSubject.value;
    const scope = this.maintenanceScopeSubject.value;
    const isAdminBypass = this.isAdminBypassSubject.value;

    // Admin bypass: never show maintenance page
    if (isAdminBypass) {
      return false;
    }

    // Server error: always show full maintenance page
    if (mode === 'server_error') {
      return true;
    }

    // Planned maintenance: only show page if scope is 'full'
    if (mode === 'planned') {
      return scope === 'full';
    }

    return false;
  }

  shouldShowInfoBanner(): boolean {
    const mode = this.maintenanceModeSubject.value;
    const scope = this.maintenanceScopeSubject.value;
    const isAdminBypass = this.isAdminBypassSubject.value;
    const initialCheckDone = this.initialCheckCompleted.value;

    // Don't show banner until initial check is completed to avoid flickering
    if (!initialCheckDone) {
      return false;
    }

    // Admin bypass: never show info banner (they get admin banner instead)
    if (isAdminBypass) {
      return false;
    }

    // Show info banner for planned maintenance with auth_only scope
    return mode === 'planned' && scope === 'auth_only';
  }

  getCurrentScope(): MaintenanceScope {
    return this.maintenanceScopeSubject.value;
  }

  isCurrentUserAdmin(): boolean {
    return this.isAdminBypassSubject.value;
  }

  ngOnDestroy() {
    this.stopPeriodicCheck();
  }
}
